import {
  BaseCondition,
  CampaignInfoView,
  CombinedCondition,
  WorkflowInfoView,
} from "@tiny/api";
import { upperFirst } from "@tiny/components/src/utils/stringUtils";
import { formatNumber } from "@tiny/components/src/utils/formatUtils";
import { formatFromUtc } from "@tiny/components/src/utils/dateUtils";

function stringToDescription(value: string | string[]) {
  if (!value) {
    return "";
  }
  if (Array.isArray(value)) {
    return `'${value[0]}'`;
  }
  return `'${value}'`;
}

function numberToDescription(value: number | number[]) {
  if (value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return formatNumber(value[0]);
  }
  return formatNumber(value);
}

function dateToDescription(value: number | number[]) {
  if (value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return formatFromUtc(new Date(value[0]), "MM/dd/yyyy");
  }
  return formatFromUtc(new Date(value), "MM/dd/yyyy");
}

function rangeNumberToDescription(value: number[]) {
  if (!value || value.length !== 2) {
    return "";
  }
  return `${formatNumber(value[0])} and ${formatNumber(value[1])}`;
}

function frequencyRangeNumberToDescription(value: number[]) {
  if (!value || value.length !== 2) {
    return "";
  }
  return `${formatNumber(value[0])} Times in ${formatNumber(value[1])} Days`;
}

function rangeDateToDescription(value: number[]) {
  if (!value || value.length !== 2) {
    return "";
  }
  const dateFrom = new Date(value[0]);
  const dateTo = new Date(value[1]);
  if (isNaN(dateFrom.getTime()) || isNaN(dateTo.getTime())) {
    return "";
  }
  const from = formatFromUtc(dateFrom, "MM/dd/yyyy");
  const to = formatFromUtc(dateTo, "MM/dd/yyyy");
  return `${from} and ${to}`;
}

function stringArrayToDescription(value: string[]) {
  if (!value) {
    return "";
  }
  return value;
}

function campaignArrayToDescription(
  value: string[],
  campaigns: CampaignInfoView[]
) {
  if (!value || campaigns.length === 0) {
    return "";
  }
  return value
    .map((id) => {
      return `'${campaigns.find((camp) => camp.id === id)?.name}'`;
    })
    .join(" and ");
}

function senderArrayToDescription(value: string[]) {
  if (!value) return "";
  return value.map((email) => `'${email}'`).join(" and ");
}

function customTextArrayToDescription(value: string[]) {
  if (!value) return "";
  return value.map((v) => `'${v}'`).join(" or ");
}

const profileDomain = {
  IN: stringArrayToDescription,
  NOT_IN: stringArrayToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
};

const profileBase = {
  KNOWN: null,
  UNKNOWN: null,
  STARTS_WITH: stringToDescription,
  NOT_START_WITH: stringToDescription,
  ENDS_WITH: stringToDescription,
  NOT_END_WITH: stringToDescription,
  CONTAINS: stringToDescription,
  NOT_CONTAIN: stringToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
};

const profileEmail = {
  IN: stringArrayToDescription,
  NOT_IN: stringArrayToDescription,
  STARTS_WITH: stringToDescription,
  NOT_START_WITH: stringToDescription,
  ENDS_WITH: stringToDescription,
  NOT_END_WITH: stringToDescription,
  CONTAINS: stringToDescription,
  NOT_CONTAIN: stringToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
  BOUNCED: null,
  NOT_BOUNCED: null,
};

const profileSource = {
  IN: stringArrayToDescription,
  NOT_IN: stringArrayToDescription,
  KNOWN: null,
  UNKNOWN: null,
  STARTS_WITH: stringToDescription,
  NOT_START_WITH: stringToDescription,
  ENDS_WITH: stringToDescription,
  NOT_END_WITH: stringToDescription,
  CONTAINS: stringToDescription,
  NOT_CONTAIN: stringToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
};

const profilePhone = {
  KNOWN: null,
  UNKNOWN: null,
  STARTS_WITH: stringToDescription,
  NOT_START_WITH: stringToDescription,
  ENDS_WITH: stringToDescription,
  NOT_END_WITH: stringToDescription,
  CONTAINS: stringToDescription,
  NOT_CONTAIN: stringToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
  VALID: null,
  NOT_VALID: null,
};

const profileDate = {
  KNOWN: null,
  UNKNOWN: null,
  IN_THE_LAST: numberToDescription,
  NOT_IN_THE_LAST: numberToDescription,
  BETWEEN: rangeDateToDescription,
  NOT_BETWEEN: rangeDateToDescription,
  BEFORE: dateToDescription,
  NOT_BEFORE: dateToDescription,
  AFTER: dateToDescription,
  NOT_AFTER: dateToDescription,
};

const profileBirthDate = {
  KNOWN: null,
  UNKNOWN: null,
  IN_THE_LAST: numberToDescription,
  NOT_IN_THE_LAST: numberToDescription,
  IN_THE_NEXT: numberToDescription,
  NOT_IN_THE_NEXT: numberToDescription,
  BETWEEN: rangeDateToDescription,
  NOT_BETWEEN: rangeDateToDescription,
  BEFORE: dateToDescription,
  NOT_BEFORE: dateToDescription,
  AFTER: dateToDescription,
  NOT_AFTER: dateToDescription,
};

const profileNumber = {
  KNOWN: null,
  UNKNOWN: null,
  GREATER_THAN: numberToDescription,
  GREATER_THAN_OR_EQUALS: numberToDescription,
  LESS_THAN: numberToDescription,
  LESS_THAN_OR_EQUALS: numberToDescription,
  BETWEEN: rangeNumberToDescription,
  NOT_BETWEEN: rangeNumberToDescription,
  EQUALS: numberToDescription,
  NOT_EQUAL: numberToDescription,
};

const profileTags = {
  KNOWN: null,
  UNKNOWN: null,
  CONTAINS: stringToDescription,
  NOT_CONTAIN: stringToDescription,
  EQUALS: stringToDescription,
  NOT_EQUAL: stringToDescription,
};

const activityBase = {
  IN_THE_LAST: numberToDescription,
  NOT_IN_THE_LAST: numberToDescription,
  BETWEEN: rangeDateToDescription,
  NOT_BETWEEN: rangeDateToDescription,
  BEFORE: dateToDescription,
  NOT_BEFORE: dateToDescription,
  AFTER: dateToDescription,
  NOT_AFTER: dateToDescription,
  AT_LEAST_ONCE_IN: numberToDescription,
  AT_LEAST_NUMBER_OF_TIMES: frequencyRangeNumberToDescription,
  NEVER: null,
};

const campaignBase = {
  IN: campaignArrayToDescription,
  NOT_IN: campaignArrayToDescription,
};
const trafficControlPercentage = {
  PERCENTAGE: numberToDescription,
  PERCENTAGE_RANGE: rangeNumberToDescription,
};

const senderBase = {
  IN: senderArrayToDescription,
  NOT_IN: senderArrayToDescription,
};

const linkUrlBase = {
  IN: customTextArrayToDescription,
};

const funcConvertValue: any = {
  PROFILE: {
    first_name: profileBase,
    last_name: profileBase,
    email: profileEmail,
    city: profileBase,
    company: profileBase,
    province: profileBase,
    country: profileBase,
    phone: profilePhone,
    postal_code: profileBase,
    created_at: profileDate,
    updated_at: profileDate,
    birthday: profileBirthDate,
    source: profileSource,
    orders_count: profileNumber,
    total_spent: profileNumber,
    domain_name: profileDomain,
    tags: profileTags,
    last_order_name: profileBase,
    last_order_date: profileDate,
    last_order_price: profileNumber,
    "Internet Service Provider": profileDomain,
  },
  ACTIVITY: {
    Emailed: activityBase,
    "Not Emailed": activityBase,
    Opened: activityBase,
    "Not Opened": activityBase,
    Clicked: activityBase,
    "Not Clicked": activityBase,
  },
  CAMPAIGN: {
    Campaign: campaignBase,
  },
  WORKFLOW: {
    Workflow: campaignBase,
  },
  TRAFFIC_CONTROL: {
    "Percentage of Subscribers": trafficControlPercentage,
  },
  SENDER: {
    Sender: senderBase,
  },
  LINK_URL: {
    "Link URL": linkUrlBase,
  },
};

const funcConvertValuePrefix: any = {};

const funcConvertValueSuffix: any = {
  IN_THE_LAST: " days",
  NOT_IN_THE_LAST: " days",
  IN_THE_NEXT: " days",
  NOT_IN_THE_NEXT: " days",
};

const isTimestamp = (v: number) => v > 1e10;

function customAttributeValueToDescription(cond: BaseCondition): string {
  const val = cond.value?.value;
  if (val === null || val === undefined) return "";
  if (Array.isArray(val)) {
    if (val.length === 0) return "";
    if (typeof val[0] === "string")
      return (val as string[]).map((v) => `'${v}'`).join(", ");
    if (val.length === 2)
      return isTimestamp(val[0])
        ? rangeDateToDescription(val as number[])
        : rangeNumberToDescription(val as number[]);
    return isTimestamp(val[0])
      ? dateToDescription(val as number[])
      : numberToDescription(val as number[]);
  }
  if (typeof val === "string") return `'${val}'`;
  if (typeof val === "number")
    return isTimestamp(val) ? dateToDescription(val) : formatNumber(val);
  return "";
}

function convertValueToDescription(
  cond: BaseCondition,
  campaigns: CampaignInfoView[]
) {
  if (
    cond.attributeType === "CUSTOM_ATTRIBUTES" ||
    cond.attributeType === "UTM"
  ) {
    return customAttributeValueToDescription(cond);
  }
  const func =
    funcConvertValue[cond.attributeType!]?.[cond.attributeName!]?.[
      cond.criteria!
    ];
  if (!func) {
    return "";
  }
  return `${funcConvertValuePrefix[cond.criteria!] || ""}${func(
    cond.value?.value,
    campaigns
  )}${funcConvertValueSuffix[cond.criteria!] || ""}`;
}

export const attributeTypeLabelMap: {
  [key: string]: string;
} = {
  ACTIVITY: "Activity",
  CAMPAIGN: "Campaign",
  AUDIENCE: "Audience",
  PROFILE: "Customer",
  SEGMENT: "Segment",
  TRAFFIC_CONTROL: "Traffic Control",
  WORKFLOW: "Workflow",
  SENDER: "Sender",
  LINK_URL: "Link URL",
  CUSTOM_ATTRIBUTES: "Custom Attributes",
  UTM: "UTM",
};

export const attributeNameLabelMap: {
  [key: string]: string;
} = {
  Campaign: "Campaign",
  Workflow: "Workflow",
  Sender: "Sender",
  "Link URL": "Link URL",
  Clicked: "Clicked",
  "Not Clicked": "Not Clicked",
  Emailed: "Emailed",
  "Not Emailed": "Not Emailed",
  Opened: "Opened",
  "Not Opened": "Not Opened",
  birthday: "Birthday",
  city: "City",
  company: "Company",
  country: "Country",
  created_at: "Created at",
  email: "Email",
  first_name: "First name",
  last_name: "Last name",
  last_order_date: "Last order date",
  last_order_name: "Last order name",
  last_order_price: "Last order price",
  orders_count: "Orders count",
  phone: "Phone",
  postal_code: "Postal code",
  province: "Province",
  source: "Source",
  tags: "Tags",
  domain_name: "Domain",
  total_spent: "Total spent",
  updated_at: "Updated at",
  "Percentage of Subscribers": "Percentage of subscribers",
  "Percentage of Subscribers in range": "Percentage of Subscribers in range",
  "Internet Service Provider": "Internet Service Provider",
  // "first_touch.utm_source": "First touch UTM source",
  // "first_touch.utm_medium": "First touch UTM medium",
  // "first_touch.utm_campaign": "First touch UTM campaign",
  // "first_touch.utm_term": "First touch UTM term",
  // "first_touch.utm_content": "First touch UTM content",
  // "first_touch.utm_id": "First touch UTM ID",
  // "last_touch.utm_source": "Last touch UTM source",
  // "last_touch.utm_medium": "Last touch UTM medium",
  // "last_touch.utm_campaign": "Last touch UTM campaign",
  // "last_touch.utm_term": "Last touch UTM term",
  // "last_touch.utm_content": "Last touch UTM content",
  // "last_touch.utm_id": "Last touch UTM ID",
};

export const criteriaLabelMap: {
  [key: string]: string;
} = {
  AFTER: "After",
  AT_LEAST_ONCE_IN: "At Least Once",
  BEFORE: "Before",
  BETWEEN: "Between",
  BOUNCED: "Bounced",
  CONTAINS: "Contains",
  CONTAINS_ALL: "Contains all",
  ENDS_WITH: "Ends with",
  EQUALS: "Equals",
  GREATER_THAN: "Greater than",
  GREATER_THAN_OR_EQUALS: "Greater than or equals",
  IN: "In",
  IN_THE_LAST: "In the last",
  KNOWN: "Is known",
  LESS_THAN: "Less than",
  LESS_THAN_OR_EQUALS: "Less than or equals",
  NEVER: "Never",
  NOT_AFTER: "Not after",
  NOT_BEFORE: "Not before",
  NOT_BETWEEN: "Not between",
  NOT_BOUNCED: "Not bounced",
  NOT_CONTAIN: "Not contains",
  NOT_CONTAINS_ALL: "Not contains all",
  NOT_END_WITH: "Not end with",
  NOT_EQUAL: "Not equals",
  NOT_IN: "Not in",
  NOT_IN_THE_LAST: "Not in the last",
  NOT_START_WITH: "Not starts with",
  NOT_VALID: "Not valid",
  STARTS_WITH: "Starts with",
  UNKNOWN: "Is unknown",
  VALID: "Valid",
  IN_THE_NEXT: "In the next",
  NOT_IN_THE_NEXT: "Not in the next",
  TODAY: "Is today",
  PERCENTAGE: "% of Subscribers",
  PERCENTAGE_RANGE: "% of Subscribers between",
  AT_LEAST_NUMBER_OF_TIMES: "At Least",
  IS: "Is",
} as any;

function baseConditionToDesc(
  cond: BaseCondition,
  campaigns: CampaignInfoView[]
) {
  if (cond.attributeType === "AUDIENCE") {
    return null;
  }
  let type = attributeTypeLabelMap[cond.attributeType!];
  let name = attributeNameLabelMap[cond.attributeName!] ?? cond.attributeName;
  let criteria = criteriaLabelMap[cond.criteria!];
  const value = convertValueToDescription(cond, campaigns);
  if (!type || !name || !criteria) {
    return null;
  }
  type = type.toLocaleLowerCase();
  name = name.toLocaleLowerCase();
  criteria = criteria.toLocaleLowerCase();
  if (cond.attributeType === "CAMPAIGN" || cond.attributeType === "WORKFLOW") {
    return `${type} ${criteria} ${value}`;
  }
  if (cond.attributeType === "TRAFFIC_CONTROL") {
    return `${type} ${value} ${name} `;
  }
  if (cond.attributeType === "SENDER" || cond.attributeType === "LINK_URL") {
    return `${name} ${criteria.toLocaleLowerCase()} ${value}`;
  }
  if (!value) {
    return `${type} ${name} ${criteria}`;
  }
  return `${type} ${name} ${criteria} ${value}`;
}

function conditonsToDescription(
  conditions: BaseCondition[],
  campaigns: CampaignInfoView[]
) {
  return conditions
    .map((cond: BaseCondition) => {
      return baseConditionToDesc(cond, campaigns);
    })
    .filter((desc: string | null) => !!desc)
    .join(" AND ");
}

export function filterToDescription(
  filter: any,
  campaignsAndWorkflows: (CampaignInfoView | WorkflowInfoView)[]
) {
  let result = "None";
  if (!filter || !filter.conditions || !filter.conditions[1]) {
    return result;
  }
  result = filter.conditions[1].conditions
    .map((cond: CombinedCondition) => {
      return conditonsToDescription(
        cond.conditions as BaseCondition[],
        campaignsAndWorkflows
      );
    })
    .join(" OR ");
  if (!result) {
    return "None";
  }
  return upperFirst(result);
}

export function extractAllCampaignsIds(filter: any) {
  let result: string[] = [];
  if (!filter) {
    return result;
  }
  function extractId(condition: any) {
    if (condition.type === "combined") {
      condition.conditions?.forEach((cond: any) => {
        extractId(cond);
      });
    } else if (
      condition.type === "base" &&
      condition.attributeType === "CAMPAIGN"
    ) {
      result = result.concat(condition.value.value);
    }
  }
  extractId(filter);
  return result;
}

export function extractAllWorkflowIds(filter: any) {
  let result: string[] = [];
  if (!filter) {
    return result;
  }
  function extractId(condition: any) {
    if (condition.type === "combined") {
      condition.conditions?.forEach((cond: any) => {
        extractId(cond);
      });
    } else if (
      condition.type === "base" &&
      condition.attributeType === "WORKFLOW"
    ) {
      result = result.concat(condition.value.value);
    }
  }
  extractId(filter);
  return result;
}
